import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/base/PageHeader";
import { usePageMeta } from "@/hooks/usePageMeta";

const services = [
  {
    icon: "ri-live-line",
    title: "实时赛事直播",
    desc: "聚合正在进行的足球赛事，实时呈现比分与场上动态，不错过每一个进球瞬间。",
  },
  {
    icon: "ri-play-circle-line",
    title: "精彩回放集锦",
    desc: "提供全场回放与精彩集锦，按联赛与日期快速检索，随时回看高光时刻。",
  },
  {
    icon: "ri-calendar-event-line",
    title: "赛程与赛果",
    desc: "覆盖英超、西甲、欧冠、世界杯等热门赛事，赛程、比分、数据一页掌握。",
  },
  {
    icon: "ri-article-line",
    title: "赛事解析",
    desc: "战术拆解、赛前前瞻与赛后复盘，帮助你用更专业的视角理解比赛。",
  },
];

const steps = [
  { no: "01", title: "选择赛事", desc: "在首页或分类页找到你感兴趣的联赛与比赛。" },
  { no: "02", title: "进入直播", desc: "点击比赛进入直播或详情页，查看实时赛况与画面区域。" },
  { no: "03", title: "收藏回看", desc: "收藏喜欢的比赛，赛后在回放专区随时重温精彩内容。" },
];

export default function About() {
  usePageMeta(
    "关于我们 - 51网站在线观看免费播播足球",
    "了解51网站在线观看免费播播足球的平台定位、服务内容与联系方式，我们致力于提供高清流畅的足球赛事直播与回放体验。",
  );

  return (
    <SiteLayout>
      <PageHeader
        title="关于我们"
        subtitle="一个专注足球赛事直播与回放的在线观赛平台，为每一位热爱足球的你，提供高清、流畅、丰富的观赛体验。"
        crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
      />

      <section className="w-full px-4 md:px-8 py-10 md:py-16 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto flex flex-col gap-14 md:gap-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-medium text-primary-700 bg-primary-100 px-3 py-1 rounded-full">
                <i className="ri-football-line" />
                平台简介
              </span>
              <h2 className="mt-4 text-2xl md:text-3xl font-semibold text-foreground-950 tracking-tight leading-snug">
                让每一场比赛，都值得被完整观看
              </h2>
              <p className="mt-5 text-sm md:text-base text-foreground-600 leading-loose">
                51网站在线观看免费播播足球诞生于一群资深球迷之手。我们深知，一场精彩比赛的魅力不仅在于比分，
                更在于战术的博弈、瞬间的爆发与全场的情绪起伏。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-loose">
                因此我们持续聚合国内外热门足球赛事，用清晰的信息结构与流畅的观看体验，
                让球迷能够快速找到想看的比赛，无论是实时直播还是赛后回放，都能轻松抵达。
              </p>
              <div className="mt-7 grid grid-cols-3 gap-4">
                {[
                  { v: "60+", l: "覆盖赛事" },
                  { v: "3000+", l: "回放资源" },
                  { v: "24/7", l: "实时更新" },
                ].map((s) => (
                  <div key={s.l} className="bg-background-100 rounded-lg p-4 text-center">
                    <p className="font-heading text-xl md:text-2xl font-semibold text-primary-700">
                      {s.v}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden bg-secondary-100">
              <img
                src="https://readdy.ai/api/search-image?query=Stylized%20football%20stadium%20aerial%20view%20with%20lush%20green%20pitch%20and%20warm%20evening%20light%2C%20editorial%20sport%20photography%20style%2C%20soft%20atmospheric%20haze%2C%20harmonious%20green%20and%20gold%20tones&width=1000&height=750&seq=about-hero-01&orientation=landscape"
                alt="足球赛场全景"
                title="51足球直播 高清赛事画面"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 tracking-tight">
                我们能为你提供什么
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-500">
                围绕观赛全流程，打造完整的内容服务
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {services.map((s) => (
                <div
                  key={s.title}
                  className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6 hover:border-primary-300 transition-colors"
                >
                  <span className="w-12 h-12 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i className={`${s.icon} text-2xl`} />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-background-100 rounded-lg p-6 md:p-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 tracking-tight">
                如何开始观赛
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-500">
                三步即可找到你想看的比赛
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {steps.map((s) => (
                <div key={s.no} className="bg-background-50 rounded-lg p-5 md:p-6 relative">
                  <span className="font-heading text-4xl font-semibold text-accent-500/30">
                    {s.no}
                  </span>
                  <h3 className="mt-2 text-base font-semibold text-foreground-950">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 tracking-tight">
                联系我们
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-loose">
                如果你在使用过程中遇到问题，或希望与我们合作，欢迎通过以下方式联系。
              </p>
              <div className="mt-6 flex flex-col gap-4">
                {[
                  { icon: "ri-mail-line", label: "商务合作邮箱", value: "service@51football.demo" },
                  { icon: "ri-phone-line", label: "服务热线", value: "400-000-5151" },
                  { icon: "ri-time-line", label: "在线服务时间", value: "每日 9:00 - 24:00" },
                ].map((c) => (
                  <div
                    key={c.label}
                    className="flex items-center gap-4 bg-background-50 border border-background-200 rounded-lg p-4"
                  >
                    <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                      <i className={`${c.icon} text-xl`} />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-400">{c.label}</p>
                      <p className="text-sm font-medium text-foreground-900">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/live"
                className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-live-line" />
                立即观看直播
              </Link>
            </div>

            <div className="bg-primary-950 rounded-lg p-6 md:p-8 text-white/75">
              <h2 className="text-xl md:text-2xl font-semibold text-white tracking-tight">
                免责声明
              </h2>
              <div className="mt-5 flex flex-col gap-4 text-sm leading-loose">
                <p>
                  本站为足球赛事内容展示与信息聚合平台，站内视频播放区域均为前端演示效果，
                  暂未接入真实直播流媒体接口，不提供任何视频源的存储与分发服务。
                </p>
                <p>
                  站内赛事名称、球队信息与数据仅用于产品功能演示，相关内容版权归原作者及赛事主办方所有。
                  如涉及版权问题，请与我们联系，我们将及时处理。
                </p>
                <p>
                  请用户自觉遵守相关法律法规，通过正规渠道观看赛事直播，本站不对第三方内容承担责任。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}